import { useCallback, useEffect, useRef, useState } from 'react';
import { cotizacionesAPI } from '../services/api';

const DEBOUNCE_MS = 2000;

/**
 * Autosave de borrador de cotizacion EN BACKEND (cross-device).
 *
 * Diseño:
 *  - Solo activo para cotizaciones NUEVAS (cotizacionId == null).
 *  - Snapshot debounced cada ~2s tras el ultimo cambio, persistido via API.
 *  - Al mount: GET /borrador → si hay, expone existingDraft para banner "Recuperar".
 *  - 1 borrador por usuario (no por device). Misma sesion en laptop = mismo draft
 *    que en celular.
 *  - Fallback silencioso: si el backend esta caido, el form sigue funcionando
 *    (no rompemos UX por un autosave).
 *
 * API (igual que la version localStorage anterior):
 *  - lastSavedAt: Date | null
 *  - existingDraft: { cotizacion, servicios, extras, savedAt } | null
 *  - dismissExistingDraft(): ignora el draft existente en esta sesion
 *  - applyDraft(setters): rehidrata el form con el draft
 *  - clearDraft(): elimina el draft (llamar tras save exitoso)
 *  - draftAge(): minutos desde el ultimo guardado
 */
export function useCotizacionDraft({
    cotizacionId,
    cotizacion,
    servicios,
    extras,
}) {
    const enabled = cotizacionId == null;
    const debounceRef = useRef(null);
    const initialMountRef = useRef(true);

    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [existingDraft, setExistingDraft] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    // Al montar: GET del borrador existente del usuario
    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await cotizacionesAPI.getBorrador();
                if (cancelled) return;
                const data = res?.data;
                if (data && data.payload) {
                    const savedAtMs = data.savedAt ? new Date(data.savedAt).getTime() : Date.now();
                    setExistingDraft({
                        ...data.payload,
                        savedAt: savedAtMs
                    });
                }
            } catch {
                // Sin draft / backend caido — silencioso, el form arranca limpio
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Snapshot debounced al backend en cada cambio relevante.
    // Saltea el primer mount para no sobreescribir antes de que el user decida.
    useEffect(() => {
        if (!enabled) return;
        if (initialMountRef.current) {
            initialMountRef.current = false;
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                await cotizacionesAPI.upsertBorrador({ cotizacion, servicios, extras });
                setLastSavedAt(new Date());
            } catch {
                // backend caido — silencioso, no bloqueamos al user
            }
        }, DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacion, servicios, extras]);

    const clearDraft = useCallback(async () => {
        try {
            await cotizacionesAPI.deleteBorrador();
        } catch {
            // ignorar
        }
        setLastSavedAt(null);
        setExistingDraft(null);
    }, []);

    const applyDraft = useCallback((setters) => {
        if (!existingDraft) return;
        setters.setCotizacion?.(existingDraft.cotizacion);
        setters.setServicios?.(existingDraft.servicios || []);
        setters.setExtras?.(existingDraft.extras || []);
        setExistingDraft(null);
    }, [existingDraft]);

    const dismissExistingDraft = useCallback(() => {
        setDismissed(true);
        // No borramos el draft del backend aqui — el user solo lo ignora ahora;
        // el autosave futuro lo sobreescribira con el estado limpio.
        setExistingDraft(null);
    }, []);

    const draftAge = useCallback(() => {
        if (!existingDraft?.savedAt) return null;
        const minutes = Math.round((Date.now() - existingDraft.savedAt) / 60000);
        return minutes;
    }, [existingDraft]);

    return {
        enabled,
        lastSavedAt,
        existingDraft: dismissed ? null : existingDraft,
        applyDraft,
        dismissExistingDraft,
        clearDraft,
        draftAge,
    };
}
